import React, { useState, useEffect } from 'react';
import { authAPI, devicesAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const [pairCode, setPairCode] = useState('');
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pairRes, childRes] = await Promise.all([
        devicesAPI.getPairCode(),
        authAPI.children()
      ]);
      setPairCode(pairRes.data.pairCode);
      setChildren(childRes.data.children);
    } catch { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  };

  const copyPairCode = () => {
    navigator.clipboard.writeText(pairCode);
    setCopied(true);
    toast.success('Pairing code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const unlinkDevice = async (childId, name) => {
    if (!window.confirm(`Unlink ${name}'s device?`)) return;
    try {
      await devicesAPI.unlink(childId);
      setChildren(prev => prev.filter(c => c._id !== childId));
      toast.success(`${name} unlinked`);
    } catch { toast.error('Failed to unlink device'); }
  };

  if (loading) return <div className="spinner" />;

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20 }}>⚙️ Settings</h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Profile Card */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">👤 Parent Account</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
            <div style={{
              width: 56, height: 56, background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
              borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 24, fontWeight: 700, color: 'white'
            }}>
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{user?.name}</div>
              <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{user?.email}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Parent Account · ID: {user?._id?.slice(-8)}</div>
            </div>
          </div>
          <button className="btn btn-outline btn-sm" onClick={logout}>🚪 Sign Out</button>
        </div>

        {/* Pairing Code */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">🔑 Device Pairing Code</div>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
            Share this code with the child device when setting up the Android app.
            The child enters this code during registration to link their account to yours.
          </p>
          <div style={{
            background: 'var(--bg-secondary)', border: '1px solid var(--border)',
            borderRadius: 12, padding: '16px 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, marginBottom: 12, fontFamily: 'monospace'
          }}>
            <span style={{ fontSize: 14, color: 'var(--blue-light)', letterSpacing: 1, wordBreak: 'break-all' }}>
              {pairCode}
            </span>
            <button className="btn btn-primary btn-sm" style={{ flexShrink: 0 }} onClick={copyPairCode}>
              {copied ? '✓ Copied' : '📋 Copy'}
            </button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            ⚠️ Keep this code private. Only share with your child's device setup.
          </div>
        </div>

        {/* Linked Devices */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">📱 Linked Child Devices</div>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{children.length} device(s)</span>
          </div>
          {children.length === 0 ? (
            <div className="empty-state" style={{ padding: 24 }}>
              <div className="empty-state-icon">📱</div>
              <h3>No Devices Linked</h3>
              <p>Install the RelationProtector Android app on the child's device and use the pairing code above.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {children.map(child => (
                <div key={child._id} style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '12px 16px', background: 'var(--bg-secondary)',
                  borderRadius: 12, border: '1px solid var(--border)'
                }}>
                  <div className="child-avatar">{child.name[0]}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{child.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{child.email}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                      Permissions: {Object.entries(child.permissions || {}).filter(([, v]) => v).map(([k]) => k).join(', ') || 'None granted'}
                    </div>
                  </div>
                  <span className={`badge ${child.isOnline ? 'badge-online' : 'badge-offline'}`}>
                    {child.isOnline ? '● Online' : '○ Offline'}
                  </span>
                  <button className="btn btn-ghost btn-sm" onClick={() => unlinkDevice(child._id, child.name)}
                    style={{ color: 'var(--red)' }}>
                    Unlink
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Privacy Notice */}
        <div className="card" style={{ background: 'rgba(59,130,246,0.05)', borderColor: 'rgba(59,130,246,0.2)' }}>
          <div className="card-title" style={{ marginBottom: 12 }}>🔒 Privacy & Transparency</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
            {[
              'All access requests require explicit real-time consent from the child device.',
              'The child device shows visible notifications whenever camera or microphone is active.',
              'All monitoring actions are logged and visible to both parent and child.',
              'Location tracking only works when the child has granted permission.',
              'SOS audio clips are only recorded when the child manually triggers an SOS alert.',
              'No background camera or microphone access — system indicators are always visible.',
              'Complies with Android policies — no hidden or covert surveillance.',
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, color: 'var(--text-secondary)' }}>
                <span style={{ color: 'var(--green)', flexShrink: 0 }}>✓</span>
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
